/**
 * Option chain data normalization, ATM detection, and Put-Call Ratio (PCR) analytics,
 * and Fast-Engine F2 real-time visible strike streaming.
 *
 * AUTHORITATIVE SEMANTICS:
 * 1. ATM Strike: rounded to the nearest valid strike step based on the underlying spot price.
 * 2. PCR (Put-Call Ratio): total Put OI / total Call OI, rounded to 2 decimal places. If Call OI is 0 -> "--".
 * 3. IV formatting: decimal fraction (e.g. 0.142) -> formatted percentage (e.g. "14.2%").
 * 4. Fast-Engine F2: Streams visible strike LTPs over multiplexed option WS; patches table cells in place on tick.
 */
import { formatIndianCompact } from "../domain/format";

export interface OptionChainStrikeRow {
  strike: number;
  ce_symbol?: string;
  ce_ltp?: number | null;
  ce_oi?: number | null;
  ce_iv?: number | null;
  pe_symbol?: string;
  pe_ltp?: number | null;
  pe_oi?: number | null;
  pe_iv?: number | null;
}

export interface PcrSummary {
  pcr: string | number;
  totalCeOi: number;
  totalPeOi: number;
}

export interface OptionChainRowView {
  strike: number;
  isAtm: boolean;
  ceIvText: string;
  ceOiText: string;
  ceLtpText: string;
  strikeText: string;
  peLtpText: string;
  peOiText: string;
  peIvText: string;
  ceSymbol: string;
  peSymbol: string;
}

/**
 * Calculates the At-The-Money (ATM) strike price rounded to the instrument's strike step.
 */
export function computeAtmStrike(spotPrice: number, strikeStep: number): number {
  if (!spotPrice || !strikeStep || strikeStep <= 0) return 0;
  return Math.round(spotPrice / strikeStep) * strikeStep;
}

/**
 * Calculates Put-Call Ratio (PCR) across all strike rows in the option chain.
 */
export function calculatePcr(strikes: OptionChainStrikeRow[]): PcrSummary {
  let totalCeOi = 0;
  let totalPeOi = 0;

  if (Array.isArray(strikes)) {
    for (const row of strikes) {
      if (!row) continue;
      if (row.ce_oi != null && !isNaN(row.ce_oi)) totalCeOi += row.ce_oi;
      if (row.pe_oi != null && !isNaN(row.pe_oi)) totalPeOi += row.pe_oi;
    }
  }

  const pcr = totalCeOi > 0 ? Math.round((totalPeOi / totalCeOi) * 100) / 100 : "--";

  return {
    pcr,
    totalCeOi,
    totalPeOi,
  };
}

/**
 * Formats Implied Volatility (IV) float as a clean percentage string.
 */
export function formatIv(iv?: number | null): string {
  if (iv == null || isNaN(iv) || iv <= 0) return "--";
  return (iv * 100).toFixed(1) + "%";
}

/**
 * Builds a standardized view-model for an option chain table row.
 */
export function buildOptionChainRowModel(
  row: OptionChainStrikeRow,
  atmStrike: number,
): OptionChainRowView {
  const isAtm = row.strike === atmStrike;
  const ceIvText = formatIv(row.ce_iv);
  const peIvText = formatIv(row.pe_iv);
  const ceOiText = row.ce_oi != null && !isNaN(row.ce_oi) ? formatIndianCompact(row.ce_oi) : "--";
  const peOiText = row.pe_oi != null && !isNaN(row.pe_oi) ? formatIndianCompact(row.pe_oi) : "--";
  const ceLtpText = row.ce_ltp != null && !isNaN(row.ce_ltp) ? row.ce_ltp.toLocaleString() : "--";
  const peLtpText = row.pe_ltp != null && !isNaN(row.pe_ltp) ? row.pe_ltp.toLocaleString() : "--";

  return {
    strike: row.strike,
    isAtm,
    ceIvText,
    ceOiText,
    ceLtpText,
    strikeText: String(row.strike),
    peLtpText,
    peOiText,
    peIvText,
    ceSymbol: row.ce_symbol || "",
    peSymbol: row.pe_symbol || "",
  };
}

/**
 * Calculates vertical scroll delta to center the ATM strike in the scrollable table wrapper.
 */
export function calculateAtmScrollDelta(
  atmRowTop: number,
  wrapTop: number,
  wrapClientHeight: number,
  atmRowHeight: number,
): number {
  const delta = atmRowTop - wrapTop;
  return delta - wrapClientHeight / 2 + atmRowHeight / 2;
}

// ══════════════════════════════════════════════════════════════════════════════
// Fast Engine F2: Real-time Visible Strike Streaming & In-place DOM Patching
// ══════════════════════════════════════════════════════════════════════════════

export interface VisibleRange {
  startIndex: number;
  endIndex: number;
}

/**
 * Computes the range of strike row indices that are visible inside a scroll container,
 * with optional buffer rows above and below for smooth streaming.
 */
export function computeVisibleStrikeIndices(
  totalRows: number,
  scrollTop: number,
  clientHeight: number,
  rowHeight: number,
  bufferRows: number = 2,
): VisibleRange {
  if (totalRows <= 0 || rowHeight <= 0) {
    return { startIndex: 0, endIndex: -1 };
  }
  const effectiveScrollTop = Math.max(0, scrollTop);
  const rawStart = Math.floor(effectiveScrollTop / rowHeight);
  const visibleCount = Math.ceil(clientHeight / rowHeight);
  const rawEnd = rawStart + visibleCount;

  const startIndex = Math.max(0, rawStart - bufferRows);
  const endIndex = Math.min(totalRows - 1, rawEnd + bufferRows);

  return { startIndex, endIndex };
}

/**
 * Extracts non-empty CE and PE symbols from a slice of strike rows.
 */
export function getVisibleSymbols(
  strikes: OptionChainStrikeRow[],
  range: VisibleRange,
): string[] {
  if (!Array.isArray(strikes) || range.startIndex > range.endIndex) return [];
  const symbols: string[] = [];
  const start = Math.max(0, range.startIndex);
  const end = Math.min(strikes.length - 1, range.endIndex);

  for (let i = start; i <= end; i++) {
    const row = strikes[i];
    if (!row) continue;
    if (row.ce_symbol && typeof row.ce_symbol === "string") {
      symbols.push(row.ce_symbol);
    }
    if (row.pe_symbol && typeof row.pe_symbol === "string") {
      symbols.push(row.pe_symbol);
    }
  }
  return symbols;
}

export interface SubscriptionDiff {
  toSubscribe: string[];
  toUnsubscribe: string[];
}

/**
 * Calculates symmetric subscription diff between currently subscribed symbols and desired symbols.
 */
export function diffSubscriptions(
  currentSymbols: Iterable<string>,
  desiredSymbols: Iterable<string>,
): SubscriptionDiff {
  const currentSet = new Set(currentSymbols);
  const desiredSet = new Set(desiredSymbols);

  const toSubscribe: string[] = [];
  const toUnsubscribe: string[] = [];

  for (const sym of desiredSet) {
    if (!currentSet.has(sym)) {
      toSubscribe.push(sym);
    }
  }

  for (const sym of currentSet) {
    if (!desiredSet.has(sym)) {
      toUnsubscribe.push(sym);
    }
  }

  return { toSubscribe, toUnsubscribe };
}

export interface CellPatchResult {
  patched: boolean;
  strike?: number;
  side?: "CE" | "PE";
  direction?: 1 | -1 | 0;
  cell?: HTMLElement;
}

/**
 * Surgically patches a single Option Chain table cell in-place.
 * Locates td[data-sym="..."], updates its textContent, and triggers a micro-animation flash
 * without re-rendering any rows or disturbing DOM focus/scroll.
 */
export function patchOptionChainLtpCell(
  tbody: HTMLElement,
  symbol: string,
  ltp: number,
  prevLtp?: number | null,
): CellPatchResult {
  if (!tbody || !symbol || ltp == null || isNaN(ltp)) {
    return { patched: false };
  }

  const escaped =
    typeof CSS !== "undefined" && typeof CSS.escape === "function"
      ? CSS.escape(symbol)
      : symbol.replace(/([ #;?%&,.+*~\':"!^$[\]()=>|\/@])/g, "\\$1");
  const cell = tbody.querySelector<HTMLElement>(`td[data-sym="${escaped}"]`);
  if (!cell) {
    return { patched: false };
  }

  const side =
    (cell.dataset.side as "CE" | "PE") || (symbol.endsWith("CE") ? "CE" : "PE");
  const tr = cell.closest("tr");
  const strike = tr && tr.dataset.strike ? parseInt(tr.dataset.strike, 10) : undefined;

  let direction: 1 | -1 | 0 = 0;
  if (prevLtp != null && !isNaN(prevLtp)) {
    if (ltp > prevLtp) direction = 1;
    else if (ltp < prevLtp) direction = -1;
  }

  cell.textContent = ltp.toLocaleString();

  cell.classList.remove("oc-cell-flash-up", "oc-cell-flash-dn");
  if (direction !== 0) {
    void cell.offsetWidth; // force DOM reflow
    cell.classList.add(direction > 0 ? "oc-cell-flash-up" : "oc-cell-flash-dn");
  }

  return {
    patched: true,
    strike,
    side,
    direction,
    cell,
  };
}

/**
 * Updates the in-memory OptionChainStrikeRow model for a given symbol.
 */
export function updateStrikeRowLtp(
  strikes: OptionChainStrikeRow[],
  symbol: string,
  ltp: number,
): { updated: boolean; prevLtp: number | null | undefined } {
  if (!Array.isArray(strikes) || !symbol || ltp == null || isNaN(ltp)) {
    return { updated: false, prevLtp: undefined };
  }

  for (const row of strikes) {
    if (!row) continue;
    if (row.ce_symbol === symbol) {
      const prevLtp = row.ce_ltp;
      row.ce_ltp = ltp;
      return { updated: true, prevLtp };
    }
    if (row.pe_symbol === symbol) {
      const prevLtp = row.pe_ltp;
      row.pe_ltp = ltp;
      return { updated: true, prevLtp };
    }
  }
  return { updated: false, prevLtp: undefined };
}

export interface StreamedOptionChainOpts {
  wrapEl: HTMLElement;
  tbodyEl: HTMLElement;
  rowHeight?: number;
  bufferRows?: number;
  sendFrame: (frame: { cmd: "subscribe" | "unsubscribe"; symbols: string[] }) => void;
}

/**
 * Encapsulates real-time option chain subscriptions, scroll-aware viewport tracking,
 * and surgical in-place DOM updates.
 */
export class StreamedOptionChainManager {
  private wrapEl: HTMLElement;
  private tbodyEl: HTMLElement;
  private rowHeight: number;
  private bufferRows: number;
  private sendFrame: (frame: { cmd: "subscribe" | "unsubscribe"; symbols: string[] }) => void;
  private currentStrikes: OptionChainStrikeRow[] = [];
  private subscribedSymbols: Set<string> = new Set();
  private scrollDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  private scrollHandler: (() => void) | null = null;
  private destroyed = false;

  constructor(opts: StreamedOptionChainOpts) {
    this.wrapEl = opts.wrapEl;
    this.tbodyEl = opts.tbodyEl;
    this.rowHeight = opts.rowHeight || 28;
    this.bufferRows = opts.bufferRows !== undefined ? opts.bufferRows : 2;
    this.sendFrame = opts.sendFrame;

    this.scrollHandler = () => this.handleScroll();
    if (this.wrapEl && typeof this.wrapEl.addEventListener === "function") {
      this.wrapEl.addEventListener("scroll", this.scrollHandler, { passive: true });
    }
  }

  public setStrikes(strikes: OptionChainStrikeRow[]): void {
    this.currentStrikes = strikes || [];
    this.syncVisibleSubscriptions();
  }

  public getSubscribedSymbols(): string[] {
    return Array.from(this.subscribedSymbols);
  }

  public handleScroll(): void {
    if (this.destroyed) return;
    if (this.scrollDebounceTimer != null) {
      clearTimeout(this.scrollDebounceTimer);
    }
    this.scrollDebounceTimer = setTimeout(() => {
      this.scrollDebounceTimer = null;
      this.syncVisibleSubscriptions();
    }, 50);
  }

  public syncVisibleSubscriptions(): void {
    if (this.destroyed || !this.wrapEl || !this.currentStrikes.length) return;

    const scrollTop = this.wrapEl.scrollTop || 0;
    const clientHeight = this.wrapEl.clientHeight || 400;
    const range = computeVisibleStrikeIndices(
      this.currentStrikes.length,
      scrollTop,
      clientHeight,
      this.rowHeight,
      this.bufferRows,
    );

    const desiredSymbols = getVisibleSymbols(this.currentStrikes, range);
    const diff = diffSubscriptions(this.subscribedSymbols, desiredSymbols);

    if (diff.toUnsubscribe.length > 0) {
      for (const s of diff.toUnsubscribe) {
        this.subscribedSymbols.delete(s);
      }
      this.sendFrame({ cmd: "unsubscribe", symbols: diff.toUnsubscribe });
    }

    if (diff.toSubscribe.length > 0) {
      for (const s of diff.toSubscribe) {
        this.subscribedSymbols.add(s);
      }
      this.sendFrame({ cmd: "subscribe", symbols: diff.toSubscribe });
    }
  }

  public onTick(symbol: string, ltp: number): CellPatchResult {
    if (this.destroyed || !symbol || ltp == null || isNaN(ltp)) {
      return { patched: false };
    }
    const { prevLtp } = updateStrikeRowLtp(this.currentStrikes, symbol, ltp);
    return patchOptionChainLtpCell(this.tbodyEl, symbol, ltp, prevLtp);
  }

  public destroy(): void {
    this.destroyed = true;
    if (this.scrollDebounceTimer != null) {
      clearTimeout(this.scrollDebounceTimer);
      this.scrollDebounceTimer = null;
    }
    if (this.wrapEl && this.scrollHandler && typeof this.wrapEl.removeEventListener === "function") {
      this.wrapEl.removeEventListener("scroll", this.scrollHandler);
      this.scrollHandler = null;
    }
    if (this.subscribedSymbols.size > 0) {
      const allSubscribed = Array.from(this.subscribedSymbols);
      this.subscribedSymbols.clear();
      this.sendFrame({ cmd: "unsubscribe", symbols: allSubscribed });
    }
    this.currentStrikes = [];
  }
}


export interface OptionChainPayload {
  pcr?: number | string;
  atm_strike?: number;
  index_ltp?: number;
  strikes?: OptionChainStrikeRow[];
  expiry_effective?: string;
  expiry_list?: string[];
  oi_source?: string;
}

export interface OrderGatingResult {
  allowed: boolean;
  reason?: string;
}

/**
 * Checks if an option chain payload is synthetic (theoretical Black-Scholes estimates)
 * generated when broker APIs are offline.
 */
export function isSyntheticChain(
  chain: OptionChainPayload | { oi_source?: string } | null | undefined
): boolean {
  if (!chain || typeof chain !== "object") return false;
  return (chain.oi_source || "").trim().toLowerCase() === "synthetic";
}

/**
 * Gates live order entry against synthetic market data.
 * Truth invariant: Real-money orders must never be placed based on fabricated/synthetic premiums.
 */
export function checkLiveOrderAllowed(
  chain: OptionChainPayload | { oi_source?: string } | null | undefined,
  isExitOrder: boolean = false
): OrderGatingResult {
  if (isExitOrder) {
    return { allowed: true };
  }
  if (isSyntheticChain(chain)) {
    return {
      allowed: false,
      reason: "Broker market data is offline. Option chain contains synthetic estimates; live order placement is blocked.",
    };
  }
  return { allowed: true };
}

/**
 * Formats a clear, unmissable warning message for synthetic market data.
 */
export function getSyntheticWarningMessage(
  chain: OptionChainPayload | { oi_source?: string } | null | undefined
): string | null {
  if (!isSyntheticChain(chain)) return null;
  return "Broker data offline: Option chain contains synthetic estimates (Black-Scholes). Real market quotes and live order placement are disabled.";
}


// ============================================================================
// PRE-BUILT STRATEGY PRESETS & PAYOFF CALCULATIONS
// ============================================================================

export type StrategyCategory = "all" | "bullish" | "bearish" | "neutral" | "volatile";

export interface StrategyPresetLegDef {
  optType: "CE" | "PE";
  side: "BUY" | "SELL";
  strikeOffsetSteps: number; // e.g. 0 (ATM), +2 (OTM +2 for Call), -2 (OTM -2 for Put)
  ratio?: number;            // default 1
}

export interface StrategyPreset {
  id: string;
  name: string;
  category: "bullish" | "bearish" | "neutral" | "volatile";
  description: string;
  outlook: string;
  riskReward: string;
  tags: string[];
  legs: StrategyPresetLegDef[];
}

export const PREBUILT_STRATEGY_PRESETS: StrategyPreset[] = [
  // --- Neutral ---
  {
    id: "short_straddle",
    name: "Short Straddle",
    category: "neutral",
    description: "Sell ATM Call and ATM Put. High theta decay when market trades in a tight range.",
    outlook: "Neutral / Low Volatility",
    riskReward: "Undefined Risk / Capped Reward (Net Credit)",
    tags: ["Theta Decay", "Range-bound", "High Win Rate"],
    legs: [
      { optType: "CE", side: "SELL", strikeOffsetSteps: 0 },
      { optType: "PE", side: "SELL", strikeOffsetSteps: 0 },
    ],
  },
  {
    id: "short_strangle",
    name: "Short Strangle",
    category: "neutral",
    description: "Sell OTM Call and OTM Put. Wide buffer zone for sideways market.",
    outlook: "Neutral / Range-bound",
    riskReward: "Undefined Risk / Capped Reward (Net Credit)",
    tags: ["Wide Buffer", "Range-bound", "High Probability"],
    legs: [
      { optType: "CE", side: "SELL", strikeOffsetSteps: 2 },
      { optType: "PE", side: "SELL", strikeOffsetSteps: -2 },
    ],
  },
  {
    id: "iron_condor",
    name: "Iron Condor",
    category: "neutral",
    description: "Sell OTM Call/Put & buy outer wings. Hedged range-bound strategy with capped risk and margin relief.",
    outlook: "Neutral / Range-bound",
    riskReward: "Defined Risk / Defined Reward (Net Credit)",
    tags: ["Hedging", "Margin Relief", "Defined Risk"],
    legs: [
      { optType: "CE", side: "SELL", strikeOffsetSteps: 2 },
      { optType: "CE", side: "BUY", strikeOffsetSteps: 4 },
      { optType: "PE", side: "SELL", strikeOffsetSteps: -2 },
      { optType: "PE", side: "BUY", strikeOffsetSteps: -4 },
    ],
  },
  {
    id: "iron_butterfly",
    name: "Iron Butterfly",
    category: "neutral",
    description: "Sell ATM Straddle and buy OTM wing hedges. High credit with strictly limited risk.",
    outlook: "Pin to ATM Strike",
    riskReward: "Defined Risk / Defined Reward (Net Credit)",
    tags: ["ATM Target", "Wing Hedges", "Defined Risk"],
    legs: [
      { optType: "CE", side: "SELL", strikeOffsetSteps: 0 },
      { optType: "CE", side: "BUY", strikeOffsetSteps: 2 },
      { optType: "PE", side: "SELL", strikeOffsetSteps: 0 },
      { optType: "PE", side: "BUY", strikeOffsetSteps: -2 },
    ],
  },

  // --- Bullish ---
  {
    id: "bull_call_spread",
    name: "Bull Call Spread",
    category: "bullish",
    description: "Buy ATM Call and sell OTM Call to fund the premium. Capped upside with limited downside.",
    outlook: "Moderately Bullish",
    riskReward: "Defined Risk / Defined Reward (Net Debit)",
    tags: ["Bullish", "Hedged Debit", "Capped Risk"],
    legs: [
      { optType: "CE", side: "BUY", strikeOffsetSteps: 0 },
      { optType: "CE", side: "SELL", strikeOffsetSteps: 2 },
    ],
  },
  {
    id: "bull_put_spread",
    name: "Bull Put Spread",
    category: "bullish",
    description: "Sell OTM Put and buy deeper OTM Put. Collect net credit as long as market stays above short strike.",
    outlook: "Mildly Bullish to Sideways",
    riskReward: "Defined Risk / Defined Reward (Net Credit)",
    tags: ["Credit Spread", "Bullish", "Theta Decay"],
    legs: [
      { optType: "PE", side: "SELL", strikeOffsetSteps: -1 },
      { optType: "PE", side: "BUY", strikeOffsetSteps: -3 },
    ],
  },
  {
    id: "long_call",
    name: "Long Call",
    category: "bullish",
    description: "Buy ATM Call for directional leverage. Max loss limited to premium paid.",
    outlook: "Strongly Bullish",
    riskReward: "Defined Risk / Unlimited Reward (Net Debit)",
    tags: ["Directional", "High Leverage", "Momentum"],
    legs: [
      { optType: "CE", side: "BUY", strikeOffsetSteps: 0 },
    ],
  },
  {
    id: "call_ratio_spread",
    name: "Call Ratio Spread (1:2)",
    category: "bullish",
    description: "Buy 1 ATM Call and sell 2 OTM Calls. Low cost or net credit bullish play targeting the short strike.",
    outlook: "Targeted Bullish",
    riskReward: "Limited Risk Downside / Undefined Far Upside",
    tags: ["Ratio Spread", "Zero/Low Cost", "Targeted"],
    legs: [
      { optType: "CE", side: "BUY", strikeOffsetSteps: 0, ratio: 1 },
      { optType: "CE", side: "SELL", strikeOffsetSteps: 2, ratio: 2 },
    ],
  },

  // --- Bearish ---
  {
    id: "bear_put_spread",
    name: "Bear Put Spread",
    category: "bearish",
    description: "Buy ATM Put and sell OTM Put. Moderately bearish play with lowered net cost and capped risk.",
    outlook: "Moderately Bearish",
    riskReward: "Defined Risk / Defined Reward (Net Debit)",
    tags: ["Bearish", "Hedged Debit", "Capped Risk"],
    legs: [
      { optType: "PE", side: "BUY", strikeOffsetSteps: 0 },
      { optType: "PE", side: "SELL", strikeOffsetSteps: -2 },
    ],
  },
  {
    id: "bear_call_spread",
    name: "Bear Call Spread",
    category: "bearish",
    description: "Sell OTM Call and buy deeper OTM Call. Collect net credit as long as market stays below short strike.",
    outlook: "Mildly Bearish to Sideways",
    riskReward: "Defined Risk / Defined Reward (Net Credit)",
    tags: ["Credit Spread", "Bearish", "Theta Decay"],
    legs: [
      { optType: "CE", side: "SELL", strikeOffsetSteps: 1 },
      { optType: "CE", side: "BUY", strikeOffsetSteps: 3 },
    ],
  },
  {
    id: "long_put",
    name: "Long Put",
    category: "bearish",
    description: "Buy ATM Put for directional downward momentum. Max loss limited to premium paid.",
    outlook: "Strongly Bearish",
    riskReward: "Defined Risk / High Reward (Net Debit)",
    tags: ["Directional", "Bearish", "Hedging"],
    legs: [
      { optType: "PE", side: "BUY", strikeOffsetSteps: 0 },
    ],
  },
  {
    id: "put_ratio_spread",
    name: "Put Ratio Spread (1:2)",
    category: "bearish",
    description: "Buy 1 ATM Put and sell 2 OTM Puts. Low cost or net credit bearish play targeting the short strike.",
    outlook: "Targeted Bearish",
    riskReward: "Limited Risk Upside / Undefined Far Downside",
    tags: ["Ratio Spread", "Low Cost", "Targeted"],
    legs: [
      { optType: "PE", side: "BUY", strikeOffsetSteps: 0, ratio: 1 },
      { optType: "PE", side: "SELL", strikeOffsetSteps: -2, ratio: 2 },
    ],
  },

  // --- Volatile / Breakout ---
  {
    id: "long_straddle",
    name: "Long Straddle",
    category: "volatile",
    description: "Buy ATM Call and ATM Put. Profits from a violent breakout in either direction or IV explosion.",
    outlook: "High Volatility / Breakout Expected",
    riskReward: "Defined Risk / Unlimited Upside & Downside (Net Debit)",
    tags: ["Event Play", "Earnings / RBI / Budget", "Long Gamma"],
    legs: [
      { optType: "CE", side: "BUY", strikeOffsetSteps: 0 },
      { optType: "PE", side: "BUY", strikeOffsetSteps: 0 },
    ],
  },
  {
    id: "long_strangle",
    name: "Long Strangle",
    category: "volatile",
    description: "Buy OTM Call and OTM Put. Cheaper entry than straddle; profits if market makes a major move beyond strikes.",
    outlook: "Explosive Move in Either Direction",
    riskReward: "Defined Risk / High Reward (Net Debit)",
    tags: ["Low Cost Breakout", "Major Move", "Long Gamma"],
    legs: [
      { optType: "CE", side: "BUY", strikeOffsetSteps: 2 },
      { optType: "PE", side: "BUY", strikeOffsetSteps: -2 },
    ],
  },
];

export function getPresetsByCategory(category: StrategyCategory): StrategyPreset[] {
  if (!category || category === "all") {
    return PREBUILT_STRATEGY_PRESETS;
  }
  return PREBUILT_STRATEGY_PRESETS.filter((p) => p.category === category);
}

export function formatStrikeOffsetLabel(offsetSteps: number, step: number): string {
  if (offsetSteps === 0) return "ATM";
  const diff = offsetSteps * step;
  return diff > 0 ? `+${diff}` : `${diff}`;
}

export interface ResolvedStrategyLeg {
  strike: number;
  optType: "CE" | "PE";
  side: "BUY" | "SELL";
  qty: number;
  ratio: number;
  symbol: string;
  ltp: number;
  offsetSteps: number;
  offsetLabel: string;
}

export interface BuildStrategyLegsOpts {
  preset: StrategyPreset | string;
  atmStrike: number;
  strikeStep: number;
  symbolPrefix?: string;
  expiry?: string;
  strikesData?: OptionChainStrikeRow[];
  lotSize?: number;
  lots?: number;
}

/**
 * Resolves a strategy preset definition into live executable legs with exact strikes,
 * symbols, market LTPs, and lot quantities.
 */
export function buildStrategyLegsFromPreset(opts: BuildStrategyLegsOpts): ResolvedStrategyLeg[] {
  const preset: StrategyPreset | undefined =
    typeof opts.preset === "string"
      ? PREBUILT_STRATEGY_PRESETS.find((p) => p.id === opts.preset)
      : opts.preset;

  if (!preset || !Array.isArray(preset.legs)) return [];

  const atm = opts.atmStrike || 0;
  const step = opts.strikeStep || 50;
  const lotSize = Math.max(1, opts.lotSize || 65);
  const lots = Math.max(1, opts.lots || 1);
  const strikesData = Array.isArray(opts.strikesData) ? opts.strikesData : [];
  const symbolPrefix = opts.symbolPrefix || "NSE:NIFTY";
  const expiry = opts.expiry || "";

  return preset.legs.map((legDef) => {
    const strike = atm + legDef.strikeOffsetSteps * step;
    const ratio = legDef.ratio || 1;
    const qty = lotSize * lots * ratio;
    const offsetLabel = formatStrikeOffsetLabel(legDef.strikeOffsetSteps, step);

    // Look up matching strike row in cached chain
    const row = strikesData.find((r) => r && r.strike === strike);
    let symbol = "";
    let ltp = 0;

    if (legDef.optType === "CE") {
      symbol = row?.ce_symbol || `${symbolPrefix}${expiry}${strike}CE`;
      ltp = row?.ce_ltp != null && !isNaN(row.ce_ltp) ? row.ce_ltp : 0;
    } else {
      symbol = row?.pe_symbol || `${symbolPrefix}${expiry}${strike}PE`;
      ltp = row?.pe_ltp != null && !isNaN(row.pe_ltp) ? row.pe_ltp : 0;
    }

    return {
      strike,
      optType: legDef.optType,
      side: legDef.side,
      qty,
      ratio,
      symbol,
      ltp: Math.max(0, ltp),
      offsetSteps: legDef.strikeOffsetSteps,
      offsetLabel,
    };
  });
}

export interface StrategyPayoffSummary {
  netPremiumPerShare: number; // positive = net credit, negative = net debit
  netPremiumTotal: number;
  isCredit: boolean;
  estMargin: number;          // margin with hedge relief
  maxProfitText: string;
  maxLossText: string;
  breakevens: number[];
  riskRewardRatio: string;
}

/**
 * Calculates net debit/credit, margin requirement with hedge discounts,
 * breakeven points, and payoff parameters for a multi-leg strategy.
 */
export function calculateStrategyPayoff(
  legs: ResolvedStrategyLeg[],
  underlyingLtp: number,
  lotSize: number = 65
): StrategyPayoffSummary {
  if (!Array.isArray(legs) || !legs.length) {
    return {
      netPremiumPerShare: 0,
      netPremiumTotal: 0,
      isCredit: false,
      estMargin: 0,
      maxProfitText: "₹0",
      maxLossText: "₹0",
      breakevens: [],
      riskRewardRatio: "N/A",
    };
  }

  let netPremPerShare = 0;
  let totalDebit = 0;
  let totalCredit = 0;
  let totalQty = 0;
  let buyLegCount = 0;
  let sellLegCount = 0;

  for (const leg of legs) {
    const ltp = leg.ltp || 0;
    const ratio = leg.ratio || 1;
    totalQty += leg.qty;

    if (leg.side === "BUY") {
      netPremPerShare -= ltp * ratio;
      totalDebit += ltp * leg.qty;
      buyLegCount++;
    } else {
      netPremPerShare += ltp * ratio;
      totalCredit += ltp * leg.qty;
      sellLegCount++;
    }
  }

  const netPremiumTotal = totalCredit - totalDebit;
  const isCredit = netPremiumTotal >= 0;

  // Margin estimation with hedge relief
  // Naked short ~1.2L per lot; Hedged short ~35k-45k per lot (approx 70% relief)
  let estMargin = 0;
  if (sellLegCount > 0) {
    const shortLots = Math.ceil(
      legs.filter((l) => l.side === "SELL").reduce((sum, l) => sum + l.qty, 0) / lotSize
    );
    const hasLongHedge = buyLegCount > 0;
    if (hasLongHedge) {
      estMargin = shortLots * 38000; // ~70% relief on hedged legs
    } else {
      estMargin = shortLots * 125000; // standard unhedged short margin
    }
  } else {
    // Pure long: margin is just the premium paid
    estMargin = Math.max(0, totalDebit);
  }

  // Calculate Breakevens and Max Profit/Loss for common combinations
  const breakevens: number[] = [];
  let maxProfitText = "--";
  let maxLossText = "--";

  if (legs.length === 1) {
    const l = legs[0];
    const prem = l.ltp;
    if (l.side === "BUY") {
      maxLossText = `₹${Math.round(prem * l.qty).toLocaleString("en-IN")}`;
      maxProfitText = "Unlimited";
      if (l.optType === "CE") breakevens.push(l.strike + prem);
      else breakevens.push(l.strike - prem);
    } else {
      maxProfitText = `₹${Math.round(prem * l.qty).toLocaleString("en-IN")}`;
      maxLossText = "Unlimited";
      if (l.optType === "CE") breakevens.push(l.strike + prem);
      else breakevens.push(l.strike - prem);
    }
  } else if (legs.length === 2) {
    const [l1, l2] = legs;
    // Straddle (same strike, opposite optType)
    if (l1.strike === l2.strike && l1.optType !== l2.optType && l1.side === l2.side) {
      const combinedPrem = l1.ltp + l2.ltp;
      breakevens.push(Math.round((l1.strike - combinedPrem) * 100) / 100);
      breakevens.push(Math.round((l1.strike + combinedPrem) * 100) / 100);
      if (l1.side === "SELL") {
        maxProfitText = `₹${Math.round(combinedPrem * l1.qty).toLocaleString("en-IN")}`;
        maxLossText = "Unlimited";
      } else {
        maxLossText = `₹${Math.round(combinedPrem * l1.qty).toLocaleString("en-IN")}`;
        maxProfitText = "Unlimited";
      }
    }
    // Vertical Spread (same optType, different strikes, one BUY and one SELL)
    else if (l1.optType === l2.optType && l1.side !== l2.side) {
      const buyLeg = l1.side === "BUY" ? l1 : l2;
      const sellLeg = l1.side === "SELL" ? l1 : l2;
      const strikeDiff = Math.abs(buyLeg.strike - sellLeg.strike);
      const netCost = Math.abs(netPremiumTotal);
      const maxSpreadValue = strikeDiff * buyLeg.qty;

      if (isCredit) {
        maxProfitText = `₹${Math.round(netCost).toLocaleString("en-IN")}`;
        const maxLoss = Math.max(0, maxSpreadValue - netCost);
        maxLossText = `₹${Math.round(maxLoss).toLocaleString("en-IN")}`;
        breakevens.push(sellLeg.strike - (netCost / buyLeg.qty));
      } else {
        maxLossText = `₹${Math.round(netCost).toLocaleString("en-IN")}`;
        const maxProfit = Math.max(0, maxSpreadValue - netCost);
        maxProfitText = `₹${Math.round(maxProfit).toLocaleString("en-IN")}`;
        if (buyLeg.optType === "CE") {
          breakevens.push(buyLeg.strike + (netCost / buyLeg.qty));
        } else {
          breakevens.push(buyLeg.strike - (netCost / buyLeg.qty));
        }
      }
    }
    // Strangle (different strikes, different optTypes, same side)
    else if (l1.side === l2.side && l1.optType !== l2.optType) {
      const callLeg = l1.optType === "CE" ? l1 : l2;
      const putLeg = l1.optType === "PE" ? l1 : l2;
      const combinedPrem = callLeg.ltp + putLeg.ltp;
      breakevens.push(Math.round((putLeg.strike - combinedPrem) * 100) / 100);
      breakevens.push(Math.round((callLeg.strike + combinedPrem) * 100) / 100);
      if (l1.side === "SELL") {
        maxProfitText = `₹${Math.round(combinedPrem * callLeg.qty).toLocaleString("en-IN")}`;
        maxLossText = "Unlimited";
      } else {
        maxLossText = `₹${Math.round(combinedPrem * callLeg.qty).toLocaleString("en-IN")}`;
        maxProfitText = "Unlimited";
      }
    }
  } else if (legs.length === 4) {
    // Iron Condor / Butterfly
    const netCredit = Math.max(0, netPremiumTotal);
    const shortCalls = legs.filter((l) => l.side === "SELL" && l.optType === "CE");
    const longCalls = legs.filter((l) => l.side === "BUY" && l.optType === "CE");
    const shortPuts = legs.filter((l) => l.side === "SELL" && l.optType === "PE");
    const longPuts = legs.filter((l) => l.side === "BUY" && l.optType === "PE");

    if (shortCalls.length && longCalls.length && shortPuts.length && longPuts.length) {
      const callWing = Math.abs(longCalls[0].strike - shortCalls[0].strike);
      const putWing = Math.abs(shortPuts[0].strike - longPuts[0].strike);
      const wingWidth = Math.min(callWing, putWing);
      const totalSpreadRisk = wingWidth * shortCalls[0].qty;
      const maxLoss = Math.max(0, totalSpreadRisk - netCredit);

      maxProfitText = `₹${Math.round(netCredit).toLocaleString("en-IN")}`;
      maxLossText = `₹${Math.round(maxLoss).toLocaleString("en-IN")}`;

      const premPerShare = netCredit / shortCalls[0].qty;
      breakevens.push(Math.round((shortPuts[0].strike - premPerShare) * 100) / 100);
      breakevens.push(Math.round((shortCalls[0].strike + premPerShare) * 100) / 100);
    }
  }

  // Fallbacks if not computed above
  if (maxProfitText === "--") {
    maxProfitText = isCredit ? `₹${Math.round(netPremiumTotal).toLocaleString("en-IN")}` : "Undefined";
  }
  if (maxLossText === "--") {
    maxLossText = isCredit ? "Undefined" : `₹${Math.round(Math.abs(netPremiumTotal)).toLocaleString("en-IN")}`;
  }

  return {
    netPremiumPerShare: Math.round(netPremPerShare * 100) / 100,
    netPremiumTotal: Math.round(netPremiumTotal),
    isCredit,
    estMargin: Math.round(estMargin),
    maxProfitText,
    maxLossText,
    breakevens: breakevens.sort((a, b) => a - b),
    riskRewardRatio: isCredit ? "Credit (Net Inflow)" : "Debit (Net Outflow)",
  };
}
